/**
 * Digest Engagement — outcome tracking for the governed weekly digest.
 *
 * Mirrors the proposalEngagement pattern (open / reply signals recorded
 * verbatim, never invented) but keyed per SEND: every sealed digest send
 * (`sendId` from digestSendPipeline.approveAndSend) accrues an honest
 * {@link DigestOutcome} as engagement events arrive, and
 * {@link digestEfficacy} rolls the book up into open/reply rates plus a
 * precision figure — so "did anyone actually read these?" has a real
 * answer instead of a phantom chart.
 *
 * State is an in-module registry (session-local, like the approval-undo
 * pattern in approvals.ts) with an explicit {@link resetDigestEngagement}
 * so tests stay deterministic. Transitions are monotonic and honest:
 * an open proves delivery, a reply proves an open — but a booked meeting
 * proves only itself; we never infer engagement that wasn't observed.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The cumulative outcome of one sent digest. */
export type DigestOutcome = {
  sent: boolean;
  opened: boolean;
  replied: boolean;
  meetingBooked: boolean;
};

/** One observed engagement event — recorded verbatim, never invented. */
export type DigestEngagementEvent = 'sent' | 'opened' | 'replied' | 'meeting_booked';

/** A fresh, all-false outcome. */
export function emptyOutcome(): DigestOutcome {
  return { sent: false, opened: false, replied: false, meetingBooked: false };
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const registry = new Map<string, DigestOutcome>();

/** Read a send's current outcome (all-false when nothing recorded yet). */
export function getDigestOutcome(sendId: string): DigestOutcome {
  return registry.get(sendId) ?? emptyOutcome();
}

/** Every tracked outcome — feed straight into {@link digestEfficacy}. */
export function allDigestOutcomes(): DigestOutcome[] {
  return [...registry.values()].map((o) => ({ ...o }));
}

/** Clear the session registry — deterministic test isolation. */
export function resetDigestEngagement(): void {
  registry.clear();
}

/**
 * Record one engagement event against a send and return the updated
 * outcome. Transitions are monotonic (a flag never un-sets) and honest:
 *
 *   sent            → sent
 *   opened          → sent + opened            (an open proves delivery)
 *   replied         → sent + opened + replied  (a reply proves it was read)
 *   meeting_booked  → sent + meetingBooked     (proves only itself)
 *
 * The returned object is a copy — callers can't mutate the registry.
 */
export function recordEngagement(sendId: string, event: DigestEngagementEvent): DigestOutcome {
  const prev = registry.get(sendId) ?? emptyOutcome();
  const next: DigestOutcome = { ...prev, sent: true };
  switch (event) {
    case 'sent':
      break;
    case 'opened':
      next.opened = true;
      break;
    case 'replied':
      next.opened = true;
      next.replied = true;
      break;
    case 'meeting_booked':
      next.meetingBooked = true;
      break;
  }
  registry.set(sendId, next);
  return { ...next };
}

// ---------------------------------------------------------------------------
// Efficacy rollup
// ---------------------------------------------------------------------------

export interface DigestEfficacy {
  /** Fraction of sent digests that were opened (0 when nothing sent). */
  openRate: number;
  /** Fraction of sent digests that drew a reply. */
  replyRate: number;
  /** Fraction of sent digests that produced a real outcome (reply OR meeting). */
  precision: number;
}

/**
 * Roll a book of outcomes up into the engagement badge's numbers. Pure and
 * total: an empty or never-sent book reports honest zeros, never NaN.
 */
export function digestEfficacy(outcomes: DigestOutcome[]): DigestEfficacy {
  const sent = outcomes.filter((o) => o.sent);
  if (sent.length === 0) return { openRate: 0, replyRate: 0, precision: 0 };
  const opened = sent.filter((o) => o.opened).length;
  const replied = sent.filter((o) => o.replied).length;
  const meaningful = sent.filter((o) => o.replied || o.meetingBooked).length;
  return {
    openRate: opened / sent.length,
    replyRate: replied / sent.length,
    precision: meaningful / sent.length,
  };
}
